import React, {useState, useEffect} from 'react';
import emailjs from '@emailjs/browser';
import {config} from '../config.ts';
import ReactDOM from "react-dom/client";
import {renderTextField, renderTextArea, renderEmailField, renderDropdown} from "./fields/FormFields.tsx";
import {setElementAttribute} from "../utils/common-utils.ts";

export const renderFormFeedbackEn = (div: string) => {
    const root = document.getElementById(div)
    if (root) {
        ReactDOM.createRoot(root).render(<React.StrictMode><FormFeedbackEn/></React.StrictMode>)
    }
}
const FormFeedbackEn = () => {
    const [name, setName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [issueType, setIssueType] = useState('select');
    const [otherIssue, setOtherIssue] = useState('');
    const [subject, setSubject] = useState('');
    const [feedback, setFeedback] = useState('');
    const [messageSent, setMessageSent] = useState(false);
    const [messageError, setMessageError] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
      if (messageSent) {
        document.getElementById("component-form-accessibility-en")?.focus();
      }
    }, [messageSent]);

    const renderName = () => {
        const inputData = {
            name: 'Your name',
            id: 'name',
            autoComplete: 'name',
            label: 'Name',
            type: 'text',
            inputMode: 'text',
            value: '',
            required: false,
            description: 'field_description_name',
            message: 'If you wish to provide feedback anonymously, please leave the name field blank.',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, name, setName, submitted);
    }

    const renderEmailDetails = () => {
        const inputData = {
            name: 'email',
            id: 'email',
            label: 'Email',
            type: 'email',
            autoComplete: 'email',
            required: false,
            description: 'field_description_email',
            message: 'If you wish to provide feedback anonymously, please leave the email field blank.',
            errorMessageID: '',
            visible: true,
            success: 'Thank You for your email address.'
        }
        return renderEmailField(inputData, emailAddress, setEmailAddress, submitted);
    }

    const renderPhone = () => {
        const inputData = {
            name: 'phone',
            id: 'phone',
            label: 'Telephone number',
            type: 'text',
            inputMode: 'tel',
            autoComplete: 'tel-national',
            value: '',
            required: false,
            description: 'field_description_phone',
            message: 'If you wish to provide feedback anonymously, please leave the telephone number field blank.',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, phone, setPhone, submitted);
    }

    const renderIssueType = () => {
        const inputData = {
            name: 'issue-type',
            id: 'issue-type',
            label: 'Issue Type',
            option: [
                {label: '- Select -', value: 'select'},
                {label: 'Content Accessibility', value: 'Content Accessibility'},
                {label: 'Digital Accessibility (website/app)', value: 'Digital Accessibility (website/app)'},
                {label: 'Employment', value: 'Employment'},
                {label: 'CPAC facilities', value: 'CPAC facilities'},
                {label: 'Request an alternate format', value: 'Request an alternate format'},
                {label: 'Other (please specify)', value: 'Other'},
            ],
            required: true,
            description: 'field_description_issue',
            message: 'Issue type is required. Please select the option that best relates to your concern.',
            errorMessageID: 'issue_type_error_message',
            visible: true
        };

        return renderDropdown(inputData, issueType, setIssueType, submitted);
    }

    const renderOtherIssue = () => {
        const inputData = {
            name: 'other-issue',
            id: 'otherIssue',
            label: 'Issue Type - Other',
            type: 'text',
            inputMode: 'text',
            autoComplete: 'off',
            value: '',
            required: false,
            description: 'field_description_other_issue',
            message: '',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, otherIssue, setOtherIssue, submitted);
    }

    const renderSubject = () => {
        const inputData = {
            name: 'subject',
            id: 'subject',
            label: 'Subject',
            type: 'text',
            inputMode: 'text',
            autoComplete: 'off',
            value: '',
            required: true,
            description: 'field_description_subject',
            message: 'Subject is required. Please summarize your concern in less than 60 characters.',
            errorMessageID: 'subject_error_message',
            visible: true
        }
        return renderTextField(inputData, subject, setSubject, submitted);
    }

    const renderFeedback = () => {
        const inputData = {
            name: 'feedback',
            id: 'feedback',
            label: 'Feedback',
            inputMode: 'text',
            required: true,
            description: 'field_description_feedback',
            message: 'Your feedback is required. Please describe your concerns, including any relevant details such as the webpage or program involved. Feedback can be general.',
            errorMessageID: 'feedback_error_message',
            visible: true
        }
        return renderTextArea(inputData, feedback, setFeedback, submitted);
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        setSubmitted(true);
        const hasErrors = !issueType || issueType === 'select' || issueType === '' || !subject || !feedback ? true : false;

        const templateParams = {
            senderLanguage: "English",
            senderName: name ? name : "Anonymous",
            senderEmail: emailAddress ? emailAddress : "email address was not provided",
            senderPhone: phone ? phone : "phone number was not provided",
            senderIssueType: issueType === "Other" ? otherIssue : issueType !== "Other" ? issueType : "an issue was not provided",
            senderSubject: subject ? subject : "no subject provided",
            senderComments: feedback
        };
        const errorMessages = document.querySelector("#error_summary") as HTMLElement | null;
        
        if (hasErrors) {    
            setElementAttribute("error_summary", "aria-labelledby", "error_summary_heading");
            document.getElementById("error_summary")?.focus();
        } else {
            emailjs.send('outlook_service', 'feedback_template', templateParams, config.emailjsKey)
            .then(function () {
                setMessageSent(true);
                e.target.reset(); // reset the form
                setSubmitted(false);
                errorMessages?.removeAttribute("aria-labelledby");
            }, function () {
                setMessageError(true);
                document.getElementById("message_error")?.focus();
            });
        }
    }

    return (
        <>
            {
                messageSent ?
                    <h2 id='message_success' className='form_wrapper message_success' role='status'>Thank you for your submission.</h2> : messageError ?
                        <h2 id='message_error' className='form_wrapper message_fail' role='alert'>Sorry there was a problem with your submission. Please try
                            again later.</h2> :
                        <div id="form_wrapper" className="form_wrapper">
                            <div id='error_summary' aria-live="assertive" tabIndex={-1}>
                                {submitted && (!issueType || issueType === 'select' || issueType === '' || !subject || !feedback) ? <h2 id='error_summary_heading' role='alert' className='error_message invalid'>There are errors in the form.<br/><span>Please correct the errors and re-submit.</span></h2> : null}
                                <ul className="reg-list">
                                {submitted && (!issueType || issueType === 'select' || issueType === '') ? <li id='issue_type_error_message' className='error_message invalid'><a href="#issue-type"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Please select an issue type.</a></li> : null}
                                {submitted && !subject ? <li id='subject_error_message' className='error_message invalid'><a href="#subject"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Please enter a subject.</a></li> : null}
                                {submitted && !feedback ? <li id='feedback_error_message' className='error_message invalid'><a href="#feedback"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Please provide your feedback.</a></li> : null}
                                </ul>
                            </div>
                            <form aria-label="Accessibility Feedback Form" id="contact-form" onSubmit={(e) => handleSubmit(e)} itemScope
                                  itemType="https://schema.org/WebPageElement" noValidate>
                                <div className="form_body" itemProp='mainEntityOfPage'>
                                    {renderName()}
                                    {renderEmailDetails()}
                                    {renderPhone()}
                                    {renderIssueType()}
                                    {issueType === 'Other' ? renderOtherIssue() : ''}
                                    {renderSubject()}
                                    {renderFeedback()}
                                </div>
                                <div className='form_footer left_label'>
                                    <button type="submit" name='submit' value='submit' className="btn--primary">Submit</button>
                                </div>
                            </form>
                        </div>
            }
        </>
    )
}

export default FormFeedbackEn;